/* ============================================================
   節奏表（P5 · 01-run-pacing）
   ============================================================

   把「敵人隨時間變多硬」從公式換成**分段查表**。01 §三明講查表比公式好的
   理由：你可以在任何一格手動塞入節奏變化（例如第 15 分那格突然拉高），
   公式做不到 —— 而 P5 要的正是一條**有形狀**的曲線，不是單調上升。

   ---------- 這張表是怎麼算出來的（不要憑手感改） ----------

   01 §四規定的流程是「先定各階段目標 TTK → 模擬玩家 DPS → 反推敵人 HP」，
   而不是直接調 HP。實際執行時 TTK 不能用（它對密度敏感：同屏三百隻時
   每一隻都會很早被路過的 AoE 掃到一下，計時就開始了），所以改用
   **單擊佔血**＝「一隻雜兵身上最大的一次傷害 ÷ 牠的血量上限」。
   這個量同時免疫密度與持續性 AoE 的 tick 灌水，`1.0` 就是一發秒殺。

   把 01 §四的體感欄翻成單擊佔血：

     建立 0–2 分   每隻怪都是威脅   3–5 發   0.20–0.35
     起飛 2–8 分   開始能清         2–3 發   0.35–0.50
     爽感 8–15 分  碰到就死         1 發     ≥ 0.85
     危機 15–20 分 又殺不動了       2–3 發   0.33–0.50

   量測來源是 `pnpm pacing`（無敵跑滿 20 分 × 5 顆種子，
   `.claude/notes/pacing-probe.md`）。改表之前先跑它，改完再跑一次比對。

   ---------- 2026-08-06：改成**階梯**（使用者拍板） ----------

   使用者實玩回饋：「中後期（大約第九分鐘後）傷害太高，完全割草，敵人摸不到你，
   Boss 被秒殺，玩起來乏味」。報表佐證：玩家 DPS 從 2:30 的 318 漲到 19:30 的
   20,086（**63 倍**），而雜兵血量倍率只從 1.3 漲到 2.6（2 倍）——
   總工作量只跟上 10.8 倍，剩下的 6 倍差距就是「割草」。

   所以曲線從「先降後升的單一谷底」改成**五段階梯**：

     0–4 壓力   4.4 → 2.2   開場每隻都是威脅（這一段沿用調過的舊值）
     4–8 爽感   1.8 → 1.5   多而軟，割草
     8–12 壓力  2.6 → 5.0   ← 使用者說的「第九分鐘後」正是這裡
     12–16 爽感 3.2 → 3.2   落差要夠大才有「鬆一口氣」
     16–20 壓力 4.6 → 9.0   一路壓到最終 Boss 降臨（20:00）

   **三張表的分工**（這是階梯做得出來的關鍵，不是三張一起加減）：

     壓力段 = 少而硬且痛   血量↑ 傷害↑ 密度↓
     爽感段 = 多而軟不痛   血量↓ 傷害↓ 密度↑

   密度在壓力段**要降**：不降的話「硬」會退化成「一大坨血很厚的東西」，
   那不是壓力是雜訊；而爽感段的密度要拉高，割草要有東西可割。

   ---------- 為什麼舊曲線是「先降後升」（歷史，仍然適用於前 8 分鐘） ----------

   探針量到的單擊佔血是 **1.24 → 0.13 單調下降**（開場一發秒殺，
   第 16 分要打七八下）。根因不在敵人血量，在**玩家的單發傷害幾乎不成長**：
   一場 20 分鐘 DPS 漲 60 倍以上，但那幾乎全部來自「打更多下」
   （更多武裝、更快射速、更多彈），單發傷害只漲不到兩倍。

   所以這張表做兩件事：
     1. **開場拉高**（1 發 → 3 發）。順帶修掉「前 2 分鐘升 11 級」——
        01 §五那一段的目標是 5 級，殺得慢升得就慢。
     2. **中段壓低**（第 12 分谷底），把「爽感期」做出來。

   ⚠️ 第 2 點是**權宜，不是終局**。VS 的爽感期是玩家 build 成型造成的，
   不是敵人變軟造成的。真正的修法是讓單發傷害跟著成長，那是 P2（質變跳升）
   的工作。**P2 做完之後這張表的 9–15 分要往回調**，否則中段會塌掉。
   下限刻意設在 1.2（不讓敵人比自己的基礎血量還脆）就是為了留住這條線 ——
   即使把它壓到 1.2，爽感期也只到 1.4 發，剩下的缺口只有 P2 補得起來。 */

/** 每分鐘一格，索引 0…20 分。中間線性內插。 */
function at(tbl: number[], t: number) {
  var m = t / 60;
  if (m <= 0) return tbl[0];
  var i = Math.floor(m);
  if (i >= tbl.length - 1) return tbl[tbl.length - 1];
  return tbl[i] + (tbl[i + 1] - tbl[i]) * (m - i);
}

/** 雜兵血量倍率。**Boss 不吃這張表**（強度階梯直接寫在 `data/boss-stats.ts`
    的 `hp` 上，跟每一隻的預計時長並排）—— 分支在 `sim/spawn.ts`。

    對照舊的二次式 `1 + d*0.5 + d²*0.07`（d = t/200）：它從 1.0 長到 6.5，
    是一條單調上升的線。這張表在 0 分是它的 4.4 倍、12 分是它的 1/3。 */
export var HP_MUL = [
  /* ── 0–4 壓力 ──
     ⚠️ 2026-08-06 使用者實玩，**同一天降了兩次**：
     4.4 →（「最初期的小怪血太厚」）→ 3.0 →（「再更低，現在血量是光束
     都無法一次打死的情況」）→ **1.5**。

     對照實際數字（`data/enemy-stats.ts` 的基礎血 × 這張表）：

       倍率   nadel(24)  lanze(58)  geissel(72)   主武裝(18) 要幾發
       4.4      106        255         317            6
       3.0       72        174         216            4
       **1.5**   36         87         108            2

     大招「光束」是 `ultDmg: 30`（`entities/mechs.ts` 的 chassis0）——
     3.0 倍下連最小的 nadel（72）都秒不掉，那正是使用者說的狀況。

     ⚠️ **這破了這個檔原本的「下限 1.2」**（不讓敵人比自己的基礎血量還脆）。
     那條線當時的理由是「爽感期要靠 P2 質變跳升去補，不能靠砍血量」——
     P2 已經做完了（`CRAFT_MAX` 三級 ＋ 湊齊導引），所以那條線可以破。
     **開場「壓力」現在靠密度與入傷維持，不靠血量**（見另外兩張表）。

     ⚠️ 第三、四次（同一天）：1.5 → 2.0 →**2.2**，使用者連兩次說
     「再提高一些」「再高」。這兩手接在大招 +80%（`entities/mechs.ts`，
     光束 30 → 54）之後 —— 玩家的清場手段變強了，前期就有空間回調。

     **2.2 是硬上限**：nadel 53 < 大招 54，再往上「掃過去帶不走一隻」的
     鈍感就回來了。所以第四次的「再高」主要加在**入傷與密度**上，
     不是血量 —— 三張表在這一段的分工因此變成「血量到頂、威脅靠另外兩條」。

     第五次：**2.2 → 1.8**（「初期小怪血量再調低一些」）。這一次的脈絡
     跟前面四次不同 —— 使用者發現他自己的**局外部件是全滿的**
     （實測對零部件是 DPS ×1.64、結構 ×1.56、外加開局免費 4 級），
     所以「壓力太低」那幾次的體感有一部分是部件給的，不是曲線的問題。
     血量因此往回降，而入傷與密度（真正的威脅來源）留在原處。
     主控台的 `parts0` 指令可以切到零部件的基準線再試玩。 */
  1.8,  // 0   nadel ~43 HP（上限是 2.2：大招光束 54 要能一拍帶走）
  1.8,  // 1
  1.8,  // 2
  1.7,  // 3
  1.6,  // 4
  /* ── 4–8 爽感：多而軟。密度那張表在這一段往上，兩者一起才是「割草」。

     ⚠️ 2026-08-06 這一段調過兩次：先降到 0.90–1.05（配合開場降到 1.5），
     使用者實玩回報「**大概到第 8 分鐘有質變後就開始割草，0 難度，
     怪都接不進**」—— 0.90 比敵人自己的基礎血量還低，而質變正好在
     這一段到手（輸出跳 2.5–4 倍），兩件事疊起來就是「怪還沒走到就沒了」。

     現在回到 1.5–1.8：仍然明顯比前期軟（爽感還在），但撐得住質變那一跳。
     **爽感不等於零抵抗** —— 它要的是「割得動」，不是「碰到就消失」。 */
  1.80, // 5
  1.55, // 6
  1.50, // 7   最軟的一格
  1.60, // 8
  /* ── 8–12 壓力：使用者實玩說的「第九分鐘後割草」正是這裡 ──
     9 分這一格從 1.65 拉到 2.6（+58%）是刻意的陡升 —— 階梯要讓玩家
     **當場感覺到**「東西突然打不動了」，平滑過渡等於沒有階梯。 */
  /* ⚠️ 這道牆 2026-08-06 放緩過兩次，數字的來源是**兩端都量過**：

       版本                9 分   12 分   十顆種子的存活中位數
       改動前（割草）      1.65    1.3    15.5 分（使用者：「完全割草，乏味」）
       第一版（太高）       3.5    6.5     7.1 分（bot 全滅）
       第二版（還是高）     3.0    5.2     8.6 分（bot 全滅，死在 7–11.5 分）
       **現在**            2.2    3.4     ← 取中間

     兩端都試過才知道帶寬在哪：1.3 是「碰到就消失」，5.2 是「還沒拿到
     東西就撞死」。牆要有，但它的高度得對得上玩家到那時累積了多少 ——
     bot 在第 9 分大約只有 20 次決策。 */
  2.4,  // 9    ← 質變到手之後的第一道牆：對前一格是 ×1.5
  2.8,  // 10
  3.2,  // 11
  3.6,  // 12  壓力峰
  /* ── 12–16 爽感：**要比 9–12 那一段軟**，否則階梯就倒過來了。
     這一段跟著上面一起降（3.2/2.8/2.8/3.2 → 2.0/1.8/1.8/2.0）——
     `difficulty.test.ts` 抓到過一次：只降壓力段而忘了這裡，
     「爽感段比壓力段還硬」，而玩起來只會覺得「後面都差不多」。 */
  2.0,  // 13
  1.8,  // 14
  1.8,  // 15
  2.0,  // 16
  /* ── 16–20 壓力：一路壓到最終 Boss 降臨 ──
     20 分這一格 9.0 對照改動前的 2.6。理由是玩家 DPS 在這一段是
     開場的 60 倍以上，2.6 的雜兵等於不存在。 */
  4.6,  // 17
  6.2,  // 18
  7.8,  // 19
  9.0   // 20  最終 Boss 降臨；表的最後一格會被 `at()` 沿用到 20 分之後
];

/** 雜兵傷害倍率。原本等價於舊公式 `1 + d*0.2`（線性 1.0 → 2.2）。

    **2026-08-05：2–10 分砍到約 0.6×。** 經驗曲線攤平之後玩家在第 8 分鐘
    只有 16 次選擇（舊曲線是 35 次），所以那一段的威脅必須跟著降。
    這張表是三個旋鈕裡**唯一對另外兩把尺完全中性**的：

      旋鈕            入傷    單擊佔血   經驗收入
      HP_MUL          間接    **直接**   中性
      DENSITY_MUL   **直接**    無       等比減少
      DMG_MUL       **直接**    無       無

    前兩分鐘刻意不動（那時候玩家的等級進度與舊曲線幾乎相同），
    第 11 分鐘之後回到原值（新曲線在那之後的 DPS 反而比舊的高）。 */
export var DMG_MUL = [
  /* 0–4 壓力。**入傷才是「威脅」**（血量厚只是「鈍」，見 HP_MUL 的註解），
     所以前期的壓力主要放在這一條。

     ⚠️ 這一段 2026-08-06 一天內來回過三次，最後**往回收**：
     1.00 →（使用者「前期壓力太低」連兩次）→ 1.55 →
     （發現他的局外部件是全滿的）→ **1.25**。

     那幾次「再高」是照**滿部件**的體感加的，而當時滿部件對零部件是
     等效戰力 **×4.11**（`src/parts.test.ts` 的窮舉護欄）。零部件的量測 bot
     因此十顆種子全滅（存活中位數 15.5 → 4.8 分）。部件已經壓回 ×1.303，
     補償就要跟著退掉 —— 不退的話同一條曲線仍然只服務得了其中一種玩家。

     沒有退回 1.00 是因為血量那一側同時降了 59%（4.4 → 1.8，
     使用者實玩「太厚」），清場變快之後這裡有空間。 */
  1.25, // 0
  1.25, // 1
  1.20, // 2
  1.10, // 3
  1.05, // 4
  /* 4–8 爽感：**不痛**。這是「爽」的另一半 —— 只有敵人變軟而入傷不變的話，
     玩家還是要一直閃，那叫「打得動但不敢站」，不是爽感。 */
  0.70, // 5
  0.65, // 6
  0.65, // 7
  0.70, // 8
  /* 8–12 壓力：入傷是「會不會被打死」的直接旋鈕。 */
  1.10, // 9
  1.35, // 10
  1.60, // 11
  1.85, // 12
  /* 12–16 爽感 */
  1.20, // 13
  1.05, // 14
  1.05, // 15
  1.20, // 16
  /* 16–20 壓力：最後一段要真的會死人，否則最終 Boss 只是一個血包。 */
  1.75, // 17
  2.20, // 18
  2.65, // 19
  3.00  // 20
];

/** 同屏目標數的倍率（乘在 `sim/spawn.ts` 的 `targetField` 上）。

    ---------- 2026-08-05：為什麼需要第三張表 ----------

    經驗曲線攤平（`sim/level.ts`，前期 13 級 → 6 級）之後，五顆種子的存活
    中位數從 18.4 分掉到 4.0 分。逐欄比對舊新的功率曲線之後，塌掉的**不是**
    火力對血量的比（單擊佔血 2/8 分兩格甚至更好），而是這兩欄：

      分鐘      4:00        6:00        8:00
      DPS      641→363    2417→1133   4192→2514
      入傷/秒    2→6         0→5         3→11
      撐幾秒     70→23        —→28       47→13

    原因寫在這個檔上面：**單發傷害幾乎不隨等級成長，清場速度才會**。
    所以升級變慢時單擊佔血幾乎不動，而 `清場秒 = 同屏數 × 血量 ÷ DPS`
    直接翻倍 —— 場上清不掉，挨打次數就是三倍。

    `清場秒` 有兩個分子（同屏數與血量），而血量那一個已經沒有空間可以砍
    （第 6 分鐘的單擊佔血 0.54 已經超出「起飛期」的帶頂）。所以動同屏數。
    附帶它正是 01 建立期要的形狀：**少而每一隻都是威脅**，不是多而軟。

    ⚠️ 跟血量不同，**這張表不是經驗中性的**：經驗值綁在敵人實際血量上，
    所以砍密度會等比砍掉那一段的經驗收入（砍血量不會 —— 單隻變少、殺得變多，
    每秒收入大致不變）。改這張表之後升級分佈會往後漂，要重跑 `pnpm metrics`
    第 2 區確認 2–8 分那一格還在 11 級附近。

    倍率直接取「新舊 DPS 比」：第 10 分鐘之後新曲線的 DPS 反而更高
    （抽卡池不再空掉，後期還在成長），所以那之後一律 1.0。 */
export var DENSITY_MUL = [
  /* 0–4 壓力：少而硬。2026-08-06 第四次調整跟著往上 ——
     開場的基礎同屏是 10 隻，1.10 只是 11 隻，**不會變成後期那種雜訊**
     （「壓力段密度要降」那條原則是針對同屏 200+ 的中後期）。
     多一兩隻同時撲上來，比每隻多打一發更有壓迫感。 */
  1.05, // 0   同上：滿部件的補償退掉（1.10 → 1.05）
  1.05, // 1
  1.02, // 2
  0.95, // 3
  0.90, // 4
  /* 4–8 爽感：**密度往上**。割草要有東西可割 —— 血量降下來之後
     同屏數不跟著上來的話，那一段會變成「很快就清空、然後站著等」。 */
  0.85, // 5
  1.00, // 6
  1.10, // 7
  1.15, // 8
  /* 8–12 壓力：密度**要降**。不降的話「硬」會退化成一大坨血很厚的東西，
     那是雜訊不是壓力 —— 玩家要能分辨出「這一隻很難殺」而不是「好多喔」。 */
  0.85, // 9
  0.80, // 10
  0.80, // 11
  0.80, // 12
  /* 12–16 爽感 */
  1.05, // 13
  1.15, // 14
  1.15, // 15
  1.10, // 16
  /* 16–20 壓力 */
  0.85, // 17
  0.80, // 18
  0.80, // 19
  0.80  // 20
];

export function hpMulAt(t: number) { return at(HP_MUL, t); }
export function dmgMulAt(t: number) { return at(DMG_MUL, t); }
export function densityMulAt(t: number) { return at(DENSITY_MUL, t); }
